import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import { ZIndex } from "@/domains/design/constants";
import { trackEvent, TrackedEvent, type EditorReminderShillEventParams } from "@/domains/metrics";
import { buildSubscriptionPlanKindMetricsPayload } from "@/domains/metrics/monetization";
import {
  REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE,
  ReminderShillOverflowTooltipText,
} from "@/components/reminder-shill/reminder-shill-overflow-tooltip-text";
import {
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { buildReminderShillUpsellAttributionContext } from "@/modules/reminder-shill-upsell-attribution";
import { type AppStore, useAppStore } from "@/store";
import type { INoteObservable, NoteReminderShill } from "@/store/note";
import { isReminderShillFeatureFlagEnabled } from "@/store/note/reminder-shill-feature-flag";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";

interface ReminderShillActionsProps {
  noteObservable: INoteObservable;
}

interface ReminderShillEditorOverlayProps {
  noteObservable: INoteObservable;
  isSuppressedByEditorSurface: boolean;
}

export interface ReminderShillActionsViewProps {
  text: string;
  pendingAction?: "dismiss" | "click" | null;
  actionLabel?: string;
  isActionDisabled?: boolean;
  isProactiveAccessEnabled?: boolean;
  useProactiveReminderShillCopy?: boolean;
  pendingActionLabel?: string;
  onClick: () => void;
  onDismiss: () => void;
}

export const INTERNAL_REMINDER_SHILL_FEATURE_FLAG = "mem-agent-note-shills-internal";

interface ReminderShillActionButtonProps {
  "aria-label": string;
  iconKind: MDSIconKind;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
}

/**
 * Renders the editor reminder shill pill and owns its UI analytics/routing.
 */
export const ReminderShillActions = observer(function ReminderShillActions({
  noteObservable,
}: ReminderShillActionsProps) {
  const { store } = useAppStore();
  const reminderShill = noteObservable.reminderShill;
  const shouldShowReminderShill =
    isReminderShillFeatureFlagEnabled({
      getFlagStatus: flag => store.spaceAccountFeatureFlags.getFlagStatus(flag),
    }) && noteObservable.shouldShowReminderShill;
  const lastVisibleTrackedIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);
  const [pendingAction, setPendingAction] = useState<"dismiss" | "click" | null>(null);
  const hasProactiveAccess = store.subscriptions.hasProactiveAccess;
  const usesInternalReminderShillCopy =
    store.spaceAccountFeatureFlags.getFlagStatus(INTERNAL_REMINDER_SHILL_FEATURE_FLAG) === true;
  const shouldUseProactiveReminderShill = hasProactiveAccess && !usesInternalReminderShillCopy;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!reminderShill || !shouldShowReminderShill) return;
    if (lastVisibleTrackedIdRef.current === reminderShill.id) return;

    lastVisibleTrackedIdRef.current = reminderShill.id;
    trackEvent(
      TrackedEvent.EditorReminderShillPillVisible,
      buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
    );
  }, [noteObservable, reminderShill, shouldShowReminderShill, store]);

  if (!reminderShill || !shouldShowReminderShill) return null;

  const isTriggeredProactiveReminderShill =
    shouldUseProactiveReminderShill && reminderShill.is_clicked;
  if (isTriggeredProactiveReminderShill) return null;

  const handleDismiss = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setPendingAction("dismiss");

    try {
      await noteObservable.dismissReminderShill();
      trackEvent(
        TrackedEvent.EditorReminderShillPillDismissed,
        buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
      );
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setPendingAction(null);
      }
    }
  };

  const handleClick = async () => {
    if (isSubmittingRef.current || isTriggeredProactiveReminderShill) return;
    isSubmittingRef.current = true;
    setPendingAction("click");

    try {
      if (shouldUseProactiveReminderShill) {
        await noteObservable.clickReminderShill();
      } else {
        store.settingsModal.open(SettingsTabId.MemAgentPage, {
          source: "editor_reminder_shill",
          reminderShillUpsellAttributionContext: buildReminderShillUpsellAttributionContext({
            noteObservable,
            reminderShill,
            source: "editor_reminder_shill",
          }),
        });
      }
      trackEvent(
        TrackedEvent.EditorReminderShillPillSettingsLaunched,
        buildReminderShillTrackingContext({ noteObservable, reminderShill, store })
      );
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) {
        setPendingAction(null);
      }
    }
  };

  return (
    <ReminderShillActionsView
      text={reminderShill.text}
      pendingAction={pendingAction}
      actionLabel={shouldUseProactiveReminderShill ? "Create reminder" : "Open Mem Agent settings"}
      isActionDisabled={isTriggeredProactiveReminderShill}
      isProactiveAccessEnabled={shouldUseProactiveReminderShill}
      useProactiveReminderShillCopy={shouldUseProactiveReminderShill}
      pendingActionLabel={
        shouldUseProactiveReminderShill ? "Creating reminder" : "Opening Mem Agent settings"
      }
      onClick={handleClick}
      onDismiss={handleDismiss}
    />
  );
});

export const ReminderShillEditorOverlay = observer(function ReminderShillEditorOverlay({
  noteObservable,
  isSuppressedByEditorSurface,
}: ReminderShillEditorOverlayProps) {
  if (isSuppressedByEditorSurface || !noteObservable.shouldShowReminderShill) return null;

  return (
    <StyledEditorOverlay aria-live="polite">
      <ReminderShillActions noteObservable={noteObservable} />
    </StyledEditorOverlay>
  );
});

/**
 * Displays the reminder shill pill without owning sync, analytics, or routing side effects.
 */
export function ReminderShillActionsView({
  text,
  pendingAction = null,
  actionLabel = "Open Mem Agent settings",
  isActionDisabled = false,
  isProactiveAccessEnabled = false,
  useProactiveReminderShillCopy = isProactiveAccessEnabled,
  pendingActionLabel = "Opening Mem Agent settings",
  onClick,
  onDismiss,
}: ReminderShillActionsViewProps) {
  const isSubmitting = pendingAction !== null;
  const copy = useProactiveReminderShillCopy
    ? `Remind me about ${text}.`
    : `Get reminded about ${text}?`;

  return (
    <StyledContainer>
      <StyledIcon
        aria-hidden="true"
        kind={MDSIconKind.Bell}
        color={MDSIconColor.Emphasis}
        size={14}
        boxSize={16}
      />
      <StyledTextTooltip label={copy} text={copy}>
        <StyledText
          as="span"
          color={MDSTextColor.Emphasis}
          overflow={MDSTextOverflow.Ellipsis}
          variant={MDSTextVariant.TextSm}
          weight={MDSTextWeight.Smedium}
          {...{ [REMINDER_SHILL_OVERFLOW_TEXT_ATTRIBUTE]: "true" }}
        >
          {copy}
        </StyledText>
      </StyledTextTooltip>
      <StyledActions>
        <MDSTooltip
          config={{
            label: "Yes",
            placement: MDSTooltipPlacement.Top,
          }}
        >
          <ReminderShillActionButton
            aria-label={pendingAction === "click" ? pendingActionLabel : actionLabel}
            iconKind={MDSIconKind.Check}
            isDisabled={isSubmitting || isActionDisabled}
            isLoading={isSubmitting}
            onClick={onClick}
          />
        </MDSTooltip>
        <MDSTooltip
          config={{
            label: "No",
            placement: MDSTooltipPlacement.Top,
          }}
        >
          <ReminderShillActionButton
            aria-label={
              pendingAction === "dismiss"
                ? "Dismissing reminder suggestion"
                : "Dismiss reminder suggestion"
            }
            iconKind={MDSIconKind.Exit}
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            onClick={onDismiss}
          />
        </MDSTooltip>
      </StyledActions>
    </StyledContainer>
  );
}

function ReminderShillActionButton({
  "aria-label": ariaLabel,
  iconKind,
  isDisabled,
  isLoading,
  onClick,
  ...props
}: ReminderShillActionButtonProps) {
  return (
    <StyledActionButton
      {...props}
      aria-label={ariaLabel}
      iconKind={iconKind}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onClick={onClick}
      rounded
      size={MDSIconButtonSize.XSmall}
      type="button"
      variant={MDSIconButtonVariant.OnEmphasis}
    />
  );
}

/**
 * Keeps all reminder-shill workflow events on the same generated-note context.
 */
export function buildReminderShillTrackingContext({
  noteObservable,
  reminderShill,
  store,
}: {
  noteObservable: INoteObservable;
  reminderShill: NoteReminderShill;
  store: AppStore;
}): EditorReminderShillEventParams {
  return {
    context: "editor_reminder_shill",
    note_id: noteObservable.id,
    ...buildSubscriptionPlanKindMetricsPayload({ store }),
    reminder_shill_id: reminderShill.id,
    reminder_shill_text: reminderShill.text,
    reminder_shill_event_source: reminderShill.event_source,
    reminder_shill_generated_at: reminderShill.generated_at,
    reminder_shill_generated_from_note_model_version:
      reminderShill.generated_from_note_model_version,
  };
}

const StyledEditorOverlay = styled.div`
  position: absolute;
  right: 0;
  bottom: ${({ theme }) => `calc(${theme.spacing["4"]} + 32px)`};
  left: 0;
  z-index: ${ZIndex.FloatingBar};
  display: flex;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.spacing["4"]};
  pointer-events: none;
`;

const StyledContainer = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.button.emphasisLight.default};
  border-radius: 30px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text.emphasis};
  gap: ${({ theme }) => theme.spacing["1"]};
  height: 32px;
  justify-content: flex-start;
  max-width: calc(100% - 60px);
  padding: 0 ${({ theme }) => theme.spacing["3"]};
  pointer-events: auto;
`;

const StyledIcon = styled(MDSIcon)`
  flex: 0 0 16px;
`;

const StyledText = styled(MDSText)`
  flex: 1 1 auto;
  font-size: var(--font-size-text-small, 14px);
  min-width: 0;
`;

const StyledTextTooltip = styled(ReminderShillOverflowTooltipText)`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

const StyledActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0;
`;

const StyledActionButton = styled(MDSIconButton)`
  flex: 0 0 16px;
  width: 16px;
  min-width: 16px;
  height: 16px;
  min-height: 16px;
`;
